import { useEffect, useRef, useState } from 'react';
import { Play, Pause, Mic } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

interface WhatsAppAudioPlayerProps {
  storagePath?: string | null;
  externalUrl?: string | null;
  isOutbound: boolean;
  pending?: boolean;
}

const BAR_COUNT = 38;
// Pseudo-random but stable bar heights for waveform decoration
const BARS = Array.from({ length: BAR_COUNT }, (_, i) => {
  const seed = Math.sin(i * 12.9898) * 43758.5453;
  return 0.3 + (seed - Math.floor(seed)) * 0.7;
});

export function WhatsAppAudioPlayer({ storagePath, externalUrl, isOutbound, pending = false }: WhatsAppAudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  const [speed, setSpeed] = useState<1 | 1.5 | 2>(1);

  const ensureUrl = async (): Promise<string | null> => {
    if (url) return url;
    if (storagePath) {
      setLoading(true);
      try {
        const { data, error } = await supabase.storage
          .from('whatsapp-media')
          .createSignedUrl(storagePath, 3600);
        if (error) throw error;
        setUrl(data.signedUrl);
        return data.signedUrl;
      } catch (e) {
        if (externalUrl) {
          setUrl(externalUrl);
          return externalUrl;
        }
        return null;
      } finally {
        setLoading(false);
      }
    }
    if (externalUrl) {
      setUrl(externalUrl);
      return externalUrl;
    }
    return null;
  };

  const togglePlay = async () => {
    if (pending) return;
    const src = await ensureUrl();
    if (!src) return;
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
    } else {
      a.playbackRate = speed;
      try { await a.play(); } catch {}
    }
  };

  const cycleSpeed = () => {
    const next = speed === 1 ? 1.5 : speed === 1.5 ? 2 : 1;
    setSpeed(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  };

  const onSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const a = audioRef.current;
    if (!a || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    a.currentTime = Math.max(0, Math.min(duration, pct * duration));
  };

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onLoaded = () => setDuration(a.duration || 0);
    const onTime = () => setCurrent(a.currentTime || 0);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnded = () => { setPlaying(false); setCurrent(0); };
    a.addEventListener('loadedmetadata', onLoaded);
    a.addEventListener('timeupdate', onTime);
    a.addEventListener('play', onPlay);
    a.addEventListener('pause', onPause);
    a.addEventListener('ended', onEnded);
    return () => {
      a.removeEventListener('loadedmetadata', onLoaded);
      a.removeEventListener('timeupdate', onTime);
      a.removeEventListener('play', onPlay);
      a.removeEventListener('pause', onPause);
      a.removeEventListener('ended', onEnded);
    };
  }, [url]);

  const fmt = (s: number) => {
    if (!isFinite(s)) return '0:00';
    const m = Math.floor(s / 60);
    const r = Math.floor(s % 60);
    return `${m}:${r.toString().padStart(2, '0')}`;
  };

  const progress = duration ? current / duration : 0;
  const displayTime = playing || current > 0 ? fmt(current) : fmt(duration);

  return (
    <div className="flex items-center gap-2 min-w-[240px] py-1">
      {/* Avatar mic */}
      <div className="relative flex-shrink-0">
        <div className={cn(
          'w-9 h-9 rounded-full flex items-center justify-center',
          isOutbound ? 'bg-wa-green/20' : 'bg-wa-text-muted/20'
        )}>
          <Mic className={cn('w-4 h-4', isOutbound ? 'text-wa-green' : 'text-wa-text-muted')} />
        </div>
      </div>

      {/* Play/Pause */}
      <button
        onClick={togglePlay}
        disabled={loading || pending}
        className={cn(
          'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors',
          'bg-wa-text-muted/15 hover:bg-wa-text-muted/25 text-wa-text',
          (loading || pending) && 'opacity-60 cursor-not-allowed'
        )}
        aria-label={playing ? 'Pausar' : 'Reproduzir'}
      >
        {playing ? <Pause className="w-4 h-4 fill-current" /> : <Play className="w-4 h-4 fill-current ml-0.5" />}
      </button>

      {/* Waveform */}
      <div className="flex-1 flex flex-col gap-1 min-w-0">
        <div
          className="flex items-center gap-[2px] h-6 cursor-pointer"
          onClick={onSeek}
        >
          {BARS.map((h, i) => {
            const active = i / BAR_COUNT <= progress;
            return (
              <div
                key={i}
                className={cn(
                  'flex-1 rounded-full transition-colors',
                  active ? 'bg-wa-check' : 'bg-wa-text-muted/40'
                )}
                style={{ height: `${Math.max(20, h * 100)}%` }}
              />
            );
          })}
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[11px] text-wa-text-muted tabular-nums">
            {pending ? 'Enviando áudio…' : displayTime}
          </span>
          {!pending && (playing || current > 0) && (
            <button
              onClick={cycleSpeed}
              className="text-[10px] font-bold text-wa-text-muted bg-wa-text-muted/15 rounded-full px-1.5 py-0.5 hover:bg-wa-text-muted/25"
            >
              {speed}x
            </button>
          )}
        </div>
      </div>

      {url && <audio ref={audioRef} src={url} preload="metadata" />}
    </div>
  );
}
